import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

import { GlobalEventListener } from 'ui-components/components/global-event-listener';
import { emptyArray } from 'ui-components/default-props/empty';

import { AppliedFilterPropType } from '../../../prop-types/applied-filter';
import OverflowMeasurer from '../../overflow-measurer';

const MEASURE = 'measure';
const RENDER = 'render';
const OTHER_DROPDOWN_PLACEHOLDER_WIDTH = 57;
const SAVED_FILTERS_PLACEHOLDER_WIDTH = 103;
const OverflowHandler = ({
  children,
  popularIndices = emptyArray,
  requiredIndices = emptyArray,
  containerRef,
  appliedFilters = emptyArray,
  hasSavedFilterPath = false,
}) => {
  const [phase, setPhase] = useState(MEASURE);
  const [visibleIndices, setVisibleIndices] = useState(emptyArray);
  const [previousWidth, setPreviousWidth] = useState(0);
  const containerWidth = containerRef.current?.clientWidth ?? 0;
  const onMeasured = useCallback(
    (childMeasurements) => {
      let workingWidth = containerWidth;
      const visible = [];
      const overflow = [];
      // account for the Other dropdown placeholder width
      workingWidth -= OTHER_DROPDOWN_PLACEHOLDER_WIDTH;
      if (hasSavedFilterPath) {
        workingWidth -= SAVED_FILTERS_PLACEHOLDER_WIDTH;
      }
      // calculate all the required indices to get remaining width
      requiredIndices.forEach((requiredIndex) => {
        workingWidth -= childMeasurements[requiredIndex];
      });

      // iterate across all children, add in  required and any that will fit
      // then, add as many non-required children as will fit into visible
      childMeasurements.forEach((measurement, index) => {
        const isRequired = requiredIndices.includes(index);
        const isPopular = popularIndices.includes(index);
        if (isRequired || (isPopular && workingWidth >= measurement)) {
          visible.push(index);
          if (!isRequired) {
            workingWidth -= measurement;
          }
          return;
        }
        overflow.push(index);
      });

      setVisibleIndices(visible);
      setPhase(RENDER);
    },
    [containerWidth, hasSavedFilterPath, popularIndices, requiredIndices],
  );

  const triggerMeasure = useCallback(() => {
    setPreviousWidth(containerWidth);
    setPhase(MEASURE);
  }, [containerWidth]);

  useEffect(() => {
    triggerMeasure();
  }, [appliedFilters, triggerMeasure]);

  return (
    <GlobalEventListener
      onResize={() => {
        if (containerWidth !== previousWidth) {
          triggerMeasure();
        }
      }}
    >
      <>
        {phase === MEASURE && <OverflowMeasurer onMeasured={onMeasured}>{children({})}</OverflowMeasurer>}
        {phase === RENDER && children({ visibleIndices })}
      </>
    </GlobalEventListener>
  );
};

OverflowHandler.propTypes = {
  children: PropTypes.func.isRequired,
  popularIndices: PropTypes.arrayOf(PropTypes.number),
  requiredIndices: PropTypes.arrayOf(PropTypes.number),
  containerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]).isRequired,
  appliedFilters: PropTypes.arrayOf(AppliedFilterPropType),
  hasSavedFilterPath: PropTypes.bool,
};

export default OverflowHandler;
